import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  ApiActions,
  UpdateApiIsLoadingAction,
} from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import {
  createLoggedInModalPageUrl,
  createLoggedOutModalPageUrl,
} from '@app/finxone-web-frontend/app/Modules/zones/modal-utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  KybStatus,
  KycStatus,
  NavigationOptionEnum,
  UiPageConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { filter, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { setActiveRoleIfNeeded } from '../../utils/quick-link-page-url.utils';
import { updatePageTransitionWithUrl } from '../../utils/utils';
import { formatNameAsUrl, stripQueryFromPageUrl } from '../../utils/zone-url.utils';
import { CardManagementService } from '../card-management-service/card-management-service';
import { ConfigService } from '../config-service/config-service.service';
import { MetadataService } from '../metadata-service/metadata-service.service';
import { HookFunction, PageHooksService as PageHooksServiceInterface } from './page-hook.type';

@Injectable({
  providedIn: 'root',
})
export class PageHooksService implements PageHooksServiceInterface {
  private hooks: { [key: string]: HookFunction } = {};
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  accessToken: string;

  constructor(
    private metadataService: MetadataService,
    private _router: Router,
    private cardManagementService: CardManagementService,
    private store: Store,
    private configService: ConfigService,
  ) {
    this.registerHooks();
  }

  private registerHooks() {
    const methodNames = Object.getOwnPropertyNames(Object.getPrototypeOf(this));
    methodNames.forEach((methodName) => {
      if (methodName.startsWith('hook')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.hooks[methodName] = (this as any)[methodName].bind(this);
      }
    });
  }

  private async hookOrganisationKybCheck() {
    console.log('Page Hook Called: hookOrganisationKybCheck');
    const intervalId = setInterval(() => {
      this.metadataService.getUser().subscribe((profile) => {
        if (profile.organisationKybStatus === KybStatus.SUCCESS) {
          console.log('Status is completed, stopping the interval');
          clearInterval(intervalId);
          this._router.navigateByUrl('/zones/landing/home');
        } else {
          console.log('Status is not completed, checking again in 30 seconds');
        }
      });
    }, 30000);
  }

  private async hookPersonalKycCheck() {
    console.log('Page Hook Called: hookPersonalKycCheck');
    const intervalId = setInterval(() => {
      this.metadataService.getUser().subscribe((profile) => {
        if (profile.kycVerificationStatus === KycStatus.SUCCESS) {
          console.log('Status is completed, stopping the interval');
          clearInterval(intervalId);
          this._router.navigateByUrl('/zones/landing/home');
        } else {
          console.log('Status is not completed, checking again in 30 seconds');
        }
      });
    }, 30000);
  }

  private async hookCardManagementKycCheck() {
    console.log('Page Hook Called: hookCardManagementKycCheck');
    this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.getCardsKycApproval, true));
    this.cardManagementService.getCardManagementKyc().subscribe((kycApproved: boolean) => {
      this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.getCardsKycApproval, false));
      if (kycApproved) {
        console.log('Card Management Kyc is completed');
        this._router.navigateByUrl('/zones/card-management/landing-page');
      } else {
        console.log('Kyc is not completed');
      }
    });
  }

  private async hookStoreOtpOriginUrl() {
    console.log('Page Hook Called: hookStoreOtpOriginUrl');
    this.cardManagementService.originUrl = this._router.url;
  }

  private hookAutoRedirect() {
    console.log('Page Hook Called: hookAutoRedirect');
    const routeParams = this._router.url.split('/');
    // Find the index of the "zones" route segment (if it exists)
    const zoneIndex = routeParams.findIndex((param) => param === 'zones');
    // Find the index of the modal route segment (if it exists)
    const modalIndex = routeParams.findIndex((param) => param === '(modal:modal');
    // Prefer the modal index if present; otherwise fall back to the zones index
    const targetIndex = modalIndex !== -1 ? modalIndex : zoneIndex;

    const rawPageUrl = routeParams[targetIndex + 2];
    const pageUrl = stripQueryFromPageUrl(rawPageUrl);

    if (!pageUrl) {
      console.error('Could not determine pageUrl for auto redirect hook.');
      return;
    }

    //used for getting pageUrl from params to accomodate for onboarding where url is zones/onboarding/pageurl/role/default
    this.configService
      .getPageUrlPage(pageUrl)
      .pipe(
        switchMap((result) => {
          const redirectConfig = this.extractRedirectConfig(result);
          if (!redirectConfig) return of(null);

          const redirectUrl = this.buildRedirectUrl(redirectConfig);
          if (!redirectUrl) return of(null);

          return this.executeRedirect(redirectConfig, redirectUrl);
        }),
      )
      .subscribe();
  }

  private extractRedirectConfig(result: UiPageConfig | undefined) {
    if (!result?.pageConfiguration?.autoRedirectConfig?.isAutoRedirectEnabled) return null;

    return {
      timeout: Number(result?.pageConfiguration?.autoRedirectConfig?.autoRedirectTimer),
      zone: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectDestinationZone ?? '',
      destinationPageUrl: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectDestinationPageUrl ?? '',
      role: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectPersona ?? '',
      navigationOption: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectNavigationOption ?? '',
      tabRef: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectTabRef ?? '',
      externalUrl: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectExternalUrl ?? '',
      pageTransition: result?.pageConfiguration?.autoRedirectConfig?.autoRedirectPageTransition ?? '',
    };
  }

  private buildRedirectUrl(redirectConfig: {
    timeout: number;
    zone: string;
    destinationPageUrl: string;
    role: string;
    navigationOption: string;
    tabRef: string;
    externalUrl: string;
    pageTransition: string;
  }) {
    const { timeout, zone, destinationPageUrl, role, navigationOption, tabRef, externalUrl, pageTransition } =
      redirectConfig;

    if (typeof timeout !== 'number' || !zone || !destinationPageUrl) return null;

    const isLoggedOutZone =
      zone === APP_ZONES.ONBOARDING ||
      zone === APP_ZONES.ORGANISATION_ONBOARDING ||
      zone === APP_ZONES.ORGANISATION_VERIFICATION;

    return this.getRedirectUrl({
      isLoggedOutZone,
      navigationOption,
      zone,
      pageUrl: destinationPageUrl,
      role,
      tabRef,
      externalUrl,
      pageTransition,
    });
  }

  private executeRedirect(
    redirectConfig: {
      timeout: number;
      zone: string;
      destinationPageUrl: string;
      role: string;
      navigationOption: string;
      tabRef: string;
      externalUrl: string;
      pageTransition: string;
    },
    redirectUrl: string,
  ) {
    const { timeout, zone } = redirectConfig;

    //seems to be a split second faster than redirecting via the interval when timer is 0
    if (timeout === 0) {
      setActiveRoleIfNeeded(zone, this.store);
      this._router.navigateByUrl(redirectUrl);
      return of(null);
    }

    console.log(`Waiting to redirect in ${timeout} seconds)`);
    return timer(timeout * 1000).pipe(
      tap(() => {
        setActiveRoleIfNeeded(zone, this.store);
        console.log(`Auto redirecting to ${redirectUrl}`);
        this._router.navigateByUrl(redirectUrl);
      }),
      takeUntil(this._router.events.pipe(filter((event) => event instanceof NavigationStart))),
    );
  }

  private getRedirectUrl(config: {
    isLoggedOutZone: boolean;
    navigationOption: string;
    zone: string;
    pageUrl: string;
    role: string;
    tabRef: string | undefined;
    externalUrl: string | undefined;
    pageTransition: string | undefined;
  }) {
    let url = '';
    switch (config.navigationOption) {
      case NavigationOptionEnum.USE_AS_MODAL: {
        const zoneUrl = formatNameAsUrl(config.zone);
        url = config.isLoggedOutZone
          ? createLoggedOutModalPageUrl({
              zoneUrl,
              pageUrl: config.pageUrl,
              url: '',
              role: config.role,
            })
          : createLoggedInModalPageUrl({
              zoneUrl,
              pageUrl: config.pageUrl,
            });
        break;
      }

      case NavigationOptionEnum.USE_AS_NAVIGATION:
        url = config.isLoggedOutZone
          ? `/zones/${formatNameAsUrl(config.zone)}/${config.pageUrl}/${config.role}/default`
          : `/zones/${formatNameAsUrl(config.zone)}/${config.pageUrl}`;
        break;

      case NavigationOptionEnum.USE_AS_NAVIGATION_WITH_ACTIVE_TAB: {
        const tabRefPath = config.tabRef ? `#${config.tabRef}` : '';
        url = config.isLoggedOutZone
          ? `/zones/${formatNameAsUrl(config.zone)}/${config.pageUrl}/${config.role}/default${tabRefPath}`
          : `/zones/${formatNameAsUrl(config.zone)}/${config.pageUrl}${tabRefPath}`;
        break;
      }

      case NavigationOptionEnum.USE_AS_EXTERNAL_LINK: {
        url = config.externalUrl ?? '';
        break;
      }

      default:
        console.warn('Unknown navigation option:', config.navigationOption);
        break;
    }

    //Update url with pageTransition value
    url = updatePageTransitionWithUrl(url, config.navigationOption, config.pageTransition);

    return url;
  }

  public getHook(hookName: string): HookFunction | undefined {
    return this.hooks[hookName];
  }
}
